import React, { useState } from "react";
import {
  Button,
  SimpleGrid,
  VStack,
  Box,
  Text,
  Divider,
} from "@chakra-ui/react";
import { GameMode, allStageEffects, levelTimerStart } from "../types";
import WorldSelectModal from "./WorldSelectModal";

type GameStartParams = {
  mode: GameMode;
  timed?: boolean;
  worldId?: number;
};

type StartGameButtonsProps = {
  onStartGame: (params?: GameStartParams) => void;
  setGameMode: (gameMode: GameMode) => void;
  setUnlockedStageTypes: (unlockedStageTypes: string[]) => void;
  setLevelTimer: (levelTimer: number) => void;
  beginNewPuzzle: (mode?: GameMode, worldId?: number) => void;
  selectedWorldId: number;
  setSelectedWorldId: (worldId: number) => void;
  showWorldSelect?: boolean;
  setShowWorldSelect?: (show: boolean) => void;
};

type GameModeButtonProps = {
  label: string;
  colorScheme: string;
  onClick: () => void;
  icon?: string;
};

const GameModeButton: React.FC<GameModeButtonProps> = ({
  label,
  colorScheme,
  onClick,
  icon,
}) => (
  <Button
    colorScheme={colorScheme}
    size={{ base: "md", md: "lg" }}
    h={{ base: "50px", md: "56px" }}
    fontSize={{ base: "sm", md: "md" }}
    onClick={onClick}
    w="100%"
    _active={{ transform: "scale(0.98)" }}
    transition="all 0.15s"
  >
    {icon && <Text mr={2}>{icon}</Text>}
    {label}
  </Button>
);

const StartGameButtons: React.FC<StartGameButtonsProps> = ({
  onStartGame,
  setGameMode,
  setUnlockedStageTypes,
  setLevelTimer,
  beginNewPuzzle,
  selectedWorldId,
  setSelectedWorldId,
  showWorldSelect: showWorldSelectProp,
  setShowWorldSelect: setShowWorldSelectProp,
}) => {
  const [showWorldSelectInternal, setShowWorldSelectInternal] = useState(false);

  // Use props if provided, otherwise use internal state
  const showWorldSelect = showWorldSelectProp ?? showWorldSelectInternal;
  const setShowWorldSelect = setShowWorldSelectProp ?? setShowWorldSelectInternal;

  const hasEndlessMode = localStorage.getItem("unlockedEndlessMode") === "true";

  if (showWorldSelect) {
    return (
      <Box w="100%">
        <WorldSelectModal
          selectedWorldId={selectedWorldId}
          setSelectedWorldId={setSelectedWorldId}
          setGameMode={setGameMode}
          beginNewPuzzle={beginNewPuzzle}
          onStartGame={onStartGame}
          onBack={() => setShowWorldSelect(false)}
        />
      </Box>
    );
  }

  const startGame = (
    mode: GameMode,
    effects: string[] = [],
    timed: boolean = false
  ) => {
    if (timed) {
      setLevelTimer(levelTimerStart);
    }
    setGameMode(mode);
    setUnlockedStageTypes(effects);
    beginNewPuzzle(mode, selectedWorldId);
    onStartGame({ mode, timed: timed || undefined });
  };

  return (
    <VStack spacing={{ base: 4, md: 6 }} w="100%">
      {/* Main Modes */}
      <VStack spacing={2} w="100%">
        <Text
          fontSize={{ base: "xs", md: "sm" }}
          color="gray.400"
          textTransform="uppercase"
          letterSpacing="wide"
        >
          Classic Modes
        </Text>
        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} w="100%">
          <GameModeButton
            label="Standard"
            colorScheme="blue"
            onClick={() => startGame(GameMode.standard)}
          />
          <GameModeButton
            label="Classic"
            colorScheme="green"
            onClick={() => startGame(GameMode.classic)}
          />
          <GameModeButton
            label="Adventure"
            colorScheme="purple"
            onClick={() => startGame(GameMode.adventure)}
          />
        </SimpleGrid>
      </VStack>

      <Divider borderColor="whiteAlpha.200" />

      {/* Timed Modes */}
      <VStack spacing={2} w="100%">
        <Text
          fontSize={{ base: "xs", md: "sm" }}
          color="gray.400"
          textTransform="uppercase"
          letterSpacing="wide"
        >
          Timed Challenge
        </Text>
        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} w="100%">
          <GameModeButton
            label="Standard"
            colorScheme="blue"
            icon="⏱️"
            onClick={() => startGame(GameMode.standard, [], true)}
          />
          <GameModeButton
            label="Classic"
            colorScheme="green"
            icon="⏱️"
            onClick={() => startGame(GameMode.classic, [], true)}
          />
          <GameModeButton
            label="Adventure"
            colorScheme="purple"
            icon="⏱️"
            onClick={() => startGame(GameMode.adventure, [], true)}
          />
        </SimpleGrid>
      </VStack>

      {/* Endless & World Select - Only if unlocked */}
      {hasEndlessMode && (
        <>
          <Divider borderColor="whiteAlpha.200" />
          <VStack spacing={2} w="100%">
            <Text
              fontSize={{ base: "xs", md: "sm" }}
              color="gray.400"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              Unlocked Modes
            </Text>
            <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} w="100%">
              <GameModeButton
                label="Endless"
                colorScheme="red"
                onClick={() => startGame(GameMode.endless, allStageEffects)}
              />
              <GameModeButton
                label="Endless"
                colorScheme="red"
                icon="⏱️"
                onClick={() => startGame(GameMode.endless, allStageEffects, true)}
              />
              <GameModeButton
                label="Worlds"
                colorScheme="teal"
                icon="🌍"
                onClick={() => setShowWorldSelect(true)}
              />
            </SimpleGrid>
          </VStack>
        </>
      )}
    </VStack>
  );
};

export default StartGameButtons;
